/**
 * SUBSCRIPTION CARDS KOMPONENTE
 *
 * Zwei Sektionen:
 * 1. Bitcoin-Abo: Einzelne Karte für Blog-Zugang
 * 2. FCMS-Pläne: 3-spaltige Pricing-Cards (Basic, Pro, Unlimited)
 *
 * Basierend auf Tailwind UI Pricing Pattern.
 * Unterstützt Light/Dark Mode mit Brand-Farben.
 */

import React, { useState } from 'react';
import { AlertTriangle, Check, X, Loader2, TrendingUp, Tag } from 'lucide-react';
import { TWHoldButton } from '../ui/TWHoldButton';
import { TWModal, TWModalFooter, TWModalButton } from '../ui/TWModal';
import clsx from 'clsx';
import { getImageUrl } from '../../utils/imageUtils';
import type { FcmsFeatureLimits } from '../../services/fcms/fcmsLimitsService';
import type { ActiveDiscount } from '../../services/payment/subscriptionService';
import {
  getExpireJournalImpact,
  type ExpireImpactPreview,
} from '../../services/blog/blogDowngradeService';
import JournalImpactPreview from '../blog/JournalImpactPreview';

// ============================================
// TYPES
// ============================================
export interface PlanFeature {
  id: string;
  key?: string;
  name: string;
  isEnabled: boolean;
  valueLimit?: number | null;
}

export interface Plan {
  id: string;
  slug: string;
  name: string;
  description?: string;
  planType?: string;
  priceMonthly: number;
  priceYearly?: number | null;
  maxYoutubeChannels?: number | null;
  /** Max. Bilder pro Journal-Notiz. 0 = Upload deaktiviert. null = Default 3. */
  maxJournalImagesPerEntry?: number | null;
  badgeEnabled?: boolean;
  badgeText?: string | null;
  features?: PlanFeature[];
}

export type BillingInterval = 'monthly' | 'yearly';

export interface Subscription {
  id: string;
  planSlug: string;
  status: 'active' | 'trialing' | 'canceled' | 'cancelled' | 'past_due' | 'expired';
  cancelAtPeriodEnd: boolean;
  stripeSubscriptionId?: string | null;
  currentPeriodStart?: string | null;
  currentPeriodEnd?: string | null;
  trialEndsAt?: string | null;
  pendingPlanSlug?: string | null;
  pendingPlanName?: string | null;
  pendingPlanChangeAt?: string | null;
  paymentProvider?: string | null;
}

export interface SubscriptionCardsProps {
  plans: Plan[];
  subscriptions: Subscription[];
  fcmsLimits?: FcmsFeatureLimits | null;
  activeDiscount?: ActiveDiscount | null;
  onSubscribe: (planSlug: string, interval?: BillingInterval) => void;
  onCancelSubscription: (subscriptionId: string) => void;
  onReactivateSubscription: (subscriptionId: string) => void;
  onPlanChange?: (targetPlanSlug: string) => void;
  onCancelPendingDowngrade?: () => void;
  onEditResourceSelection?: () => void;
  cancellingSubscription?: string | null;
  reactivatingSubscription?: string | null;
}

// ============================================
// HELPER FUNCTIONS
// ============================================
const formatPrice = (price: number): string => {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(price);
};

/** Berechnet den rabattierten Preis basierend auf dem aktiven Discount */
const getDiscountedPrice = (price: number, discount?: ActiveDiscount | null): number | null => {
  if (!discount?.active || price === 0) return null;
  if (discount.discountType === 'percentage') {
    return price * (1 - (discount.discountValue || 0) / 100);
  }
  return Math.max(0, price - (discount.discountValue || 0));
};

/** Preis-Anzeige mit optionalem Rabatt und Intervall-Suffix */
const PriceDisplay: React.FC<{
  price: number;
  discount?: ActiveDiscount | null;
  size?: 'sm' | 'lg';
  intervalSuffix?: string;
}> = ({ price, discount, size = 'lg', intervalSuffix = '/Monat' }) => {
  const discountedPrice = getDiscountedPrice(price, discount);
  const textSize = size === 'lg' ? 'text-4xl' : 'text-3xl';

  if (price === 0) {
    return <span className={`${textSize} font-semibold tracking-tight text-gray-900 dark:text-white`}>Kostenlos</span>;
  }

  if (discountedPrice !== null) {
    return (
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Tag className="w-3.5 h-3.5 text-status-success" />
          <span className="text-xs font-medium text-green-600 dark:text-green-400">{discount!.label}</span>
        </div>
        <div className="flex items-baseline gap-x-2">
          <span className="text-lg text-gray-400 dark:text-gray-500 line-through">{formatPrice(price)}</span>
          <span className={`${textSize} font-semibold tracking-tight text-gray-900 dark:text-white`}>
            {formatPrice(discountedPrice)}
          </span>
          <span className="text-sm/6 font-semibold text-gray-500 dark:text-gray-400">{intervalSuffix}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-baseline gap-x-1">
      <span className={`${textSize} font-semibold tracking-tight text-gray-900 dark:text-white`}>
        {formatPrice(price)}
      </span>
      <span className="text-sm/6 font-semibold text-gray-500 dark:text-gray-400">{intervalSuffix}</span>
    </div>
  );
};

/**
 * Formatiert Feature-Anzeige.
 * Der Name enthält bereits die Mengenangabe (z.B. "1x Watchlist"),
 * daher wird valueLimit nicht nochmal vorangestellt.
 */
const formatFeatureLabel = (feature: PlanFeature): string => {
  return feature.name;
};

// ============================================
// SHARED: ACTION BUTTONS
// ============================================
interface ActionButtonsProps {
  plan: Plan;
  activeSub: Subscription | undefined;
  cancelledSub: Subscription | undefined;
  isFeatured: boolean;
  onSubscribe: (slug: string) => void;
  onCancelSubscription: (id: string) => void;
  onReactivateSubscription: (id: string) => void;
  onPlanChange?: (targetSlug: string) => void;
  onCancelPendingDowngrade?: () => void;
  onEditResourceSelection?: () => void;
  cancellingSubscription?: string | null;
  reactivatingSubscription?: string | null;
  // Ob ein anderer FCMS-Plan aktiv ist (für Upgrade/Downgrade Buttons)
  hasOtherFcmsActive?: boolean;
  otherFcmsActiveSub?: Subscription;
}

// =====================================================
// BITCOIN CANCEL BUTTON + DIALOG
// =====================================================
// Bei Bitcoin-Subs muss der User vor der Kündigung explizit gezeigt bekommen,
// welche Notiz-Verlinkungen gekappt werden. Daher: Klick öffnet Modal mit
// Impact-Vorschau, Schlüsselwort-Eingabe als zusätzliche Schutzschwelle.

interface BitcoinCancelButtonProps {
  subscriptionId: string;
  planName: string;
  disabled: boolean;
  onCancelSubscription: (id: string) => void;
}

const BitcoinCancelButton: React.FC<BitcoinCancelButtonProps> = ({
  subscriptionId,
  planName,
  disabled,
  onCancelSubscription,
}) => {
  const [open, setOpen] = useState(false);
  const [impact, setImpact] = useState<ExpireImpactPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [confirmInput, setConfirmInput] = useState('');
  const requiredKeyword = 'BESTÄTIGEN';

  const handleOpen = async () => {
    setOpen(true);
    setConfirmInput('');
    setLoading(true);
    try {
      const data = await getExpireJournalImpact();
      setImpact(data);
    } catch (e) {
      console.error('[BitcoinCancel] impact load failed:', e);
      setImpact({
        affectedEntryCount: 0,
        affectedReferenceCount: 0,
        sampleEntries: [],
        blogChannelCount: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (disabled) return;
    setOpen(false);
  };

  const handleConfirm = () => {
    // Schluesselwort-Check NUR wenn Notizen betroffen sind (Schutzschwelle bei
    // potenziellem Datenverlust). Ohne betroffene Notizen reicht der einfache Klick.
    if (impact && impact.affectedEntryCount > 0) {
      if (confirmInput.trim().toUpperCase() !== requiredKeyword) return;
    }
    onCancelSubscription(subscriptionId);
    setOpen(false);
  };

  const isKeywordCorrect = confirmInput.trim().toUpperCase() === requiredKeyword;

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        disabled={disabled}
        className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm
          bg-white dark:bg-gray-800 text-red-600 dark:text-red-400
          ring-1 ring-red-200 dark:ring-red-900/40
          hover:bg-red-50 dark:hover:bg-red-900/20
          transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {disabled ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
        Kündigen
      </button>

      <TWModal
        isOpen={open}
        onClose={handleClose}
        title={`${planName} kündigen`}
        icon={<AlertTriangle className="w-5 h-5 text-red-500" />}
        size="md"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Dein Abonnement läuft bis zum Periodenende weiter. Danach werden deine
            abonnierten Kanäle aus dem Newsfeed entfernt und Verlinkungen in deinen
            Notizen gekappt.
          </p>

          <JournalImpactPreview impact={impact} loading={loading} variant="cancel" />

          {impact && impact.affectedEntryCount > 0 && (
            <div className="rounded-lg border border-red-200 dark:border-red-900/40
                            bg-red-50 dark:bg-red-900/15 p-3 space-y-2">
              <label className="block text-sm font-medium text-red-800 dark:text-red-200">
                Tippe <strong>{requiredKeyword}</strong>, um die Kündigung abzuschließen
              </label>
              <input
                type="text"
                value={confirmInput}
                onChange={(e) => setConfirmInput(e.target.value)}
                placeholder={requiredKeyword}
                disabled={disabled}
                className="w-full rounded-md border border-red-300 dark:border-red-800
                  bg-white dark:bg-gray-900 px-3 py-2 text-sm
                  focus:outline-none focus:ring-2 focus:ring-red-500
                  text-gray-900 dark:text-white"
              />
            </div>
          )}
        </div>
        <TWModalFooter>
          <TWModalButton variant="cancel" onClick={handleClose} disabled={disabled}>
            Abbrechen
          </TWModalButton>
          <TWModalButton
            variant="danger"
            onClick={handleConfirm}
            disabled={
              disabled ||
              loading ||
              (impact !== null && impact.affectedEntryCount > 0 && !isKeywordCorrect)
            }
          >
            {disabled ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Kündigt...
              </>
            ) : (
              'Endgültig kündigen'
            )}
          </TWModalButton>
        </TWModalFooter>
      </TWModal>
    </>
  );
};

const ActionButtons: React.FC<ActionButtonsProps> = ({
  plan,
  activeSub,
  cancelledSub,
  isFeatured,
  onSubscribe,
  onCancelSubscription,
  onReactivateSubscription,
  onPlanChange,
  onCancelPendingDowngrade,
  onEditResourceSelection,
  cancellingSubscription,
  reactivatingSubscription,
  hasOtherFcmsActive,
  otherFcmsActiveSub,
}) => {
  const isFree = plan.planType === 'free';
  const isFcmsPlan = plan.slug?.startsWith('fcms-');
  const isBitcoinPlan = plan.slug?.startsWith('bitcoin-');
  const isChangeablePlan = isFcmsPlan || isBitcoinPlan;
  const primaryBtnClass = isFeatured
    ? 'bg-[#F7931A] text-gray-900 hover:bg-[#FFB347] active:bg-[#E8850F] focus-visible:outline-[#F7931A]'
    : 'bg-brand text-gray-900 hover:bg-brand-hover active:bg-[#D4AC0D] focus-visible:outline-brand';
  const secondaryBtnClass = 'bg-white/10 text-gray-900 dark:text-white ring-1 ring-gray-200 dark:ring-white/10 hover:bg-gray-50 dark:hover:bg-white/20';

  // Aktives Abo auf DIESEM Plan
  if (activeSub) {
    return (
      <div className="space-y-3">
        {/* Pending Downgrade Anzeige */}
        {activeSub.pendingPlanSlug && activeSub.pendingPlanChangeAt && (
          <div className="rounded-lg bg-orange-50 dark:bg-orange-900/10 border border-orange-200 dark:border-orange-800/30 p-3">
            <p className="text-xs text-orange-700 dark:text-orange-400 text-center">
              Wechsel zu <strong>{activeSub.pendingPlanName}</strong> am{' '}
              {new Date(activeSub.pendingPlanChangeAt).toLocaleDateString('de-DE')}
            </p>
            {onEditResourceSelection && (
              <button
                onClick={onEditResourceSelection}
                className="mt-2 w-full text-xs font-medium text-brand hover:text-brand-hover transition-colors"
              >
                Ressourcen-Auswahl bearbeiten
              </button>
            )}
            {onCancelPendingDowngrade && (
              <button
                onClick={onCancelPendingDowngrade}
                className="mt-1 w-full text-xs font-medium text-orange-600 dark:text-orange-400 hover:text-orange-800 dark:hover:text-orange-300 transition-colors"
              >
                Downgrade stornieren
              </button>
            )}
          </div>
        )}
        {activeSub.paymentProvider && !['free', 'free_trial'].includes(activeSub.paymentProvider) && !activeSub.pendingPlanSlug && (
          <div className="flex justify-center">
            {isBitcoinPlan ? (
              <BitcoinCancelButton
                subscriptionId={activeSub.id}
                planName={plan.name}
                disabled={cancellingSubscription === activeSub.id}
                onCancelSubscription={onCancelSubscription}
              />
            ) : (
              <TWHoldButton
                onConfirm={() => onCancelSubscription(activeSub.id)}
                icon={<X />}
                label="Kündigen"
                text="3s halten"
                disabled={cancellingSubscription === activeSub.id}
                holdDuration={3000}
                alwaysExpanded={true}
                size={34}
                expandedWidth={130}
              />
            )}
          </div>
        )}
        <p className="text-xs text-center text-gray-500 dark:text-gray-400">
          {activeSub.status === 'active' && activeSub.currentPeriodEnd && !activeSub.pendingPlanSlug && !isFree && (
            <>Nächste Zahlung: {new Date(activeSub.currentPeriodEnd).toLocaleDateString('de-DE')}</>
          )}
          {isFree && activeSub.status === 'active' && 'Aktiver kostenloser Plan'}
        </p>
      </div>
    );
  }

  // Anderer Plan derselben Familie aktiv (FCMS oder Bitcoin):
  // Upgrade/Downgrade-Button zeigen statt eines Neukaufs, damit der
  // Plan-Wechsel-Service Stripe-Proration anwendet (User zahlt nur die Differenz).
  if (isChangeablePlan && hasOtherFcmsActive && onPlanChange) {
    const otherPlanSlug = otherFcmsActiveSub?.planSlug;
    const otherPaymentProvider = otherFcmsActiveSub?.paymentProvider;
    const otherIsFreePlan = !otherPaymentProvider || otherPaymentProvider === 'free' || otherPaymentProvider === 'free_trial';
    const familyOrder = isBitcoinPlan
      ? ['bitcoin-stacker', 'bitcoin-whale']
      : ['fcms-basic', 'fcms-pro', 'fcms-unlimited'];
    const currentIdx = familyOrder.indexOf(otherPlanSlug || '');
    const targetIdx = familyOrder.indexOf(plan.slug);
    const isUpgrade = targetIdx > currentIdx;

    return (
      <div className="space-y-3">
        <button
          onClick={() => {
            // Upgrade von kostenlosem Plan: Normaler Checkout-Flow
            // (Backend cancelt den Free-Plan automatisch)
            if (otherIsFreePlan && isUpgrade) {
              onSubscribe(plan.slug);
            } else {
              onPlanChange(plan.slug);
            }
          }}
          className={clsx(
            'w-full rounded-lg px-4 py-2.5 text-sm font-semibold transition-all focus-visible:outline-2 focus-visible:outline-offset-2',
            isUpgrade
              ? primaryBtnClass
              : secondaryBtnClass
          )}
        >
          {isUpgrade ? 'Upgraden' : 'Downgraden'}
        </button>
      </div>
    );
  }

  if (cancelledSub) {
    // Solange das Abo noch laeuft (currentPeriodEnd in Zukunft) -> reaktivieren
    // (Stripe: cancel_at_period_end = false). Sonst neu abonnieren.
    const stillActive = cancelledSub.currentPeriodEnd
      ? new Date(cancelledSub.currentPeriodEnd) > new Date()
      : false;
    const isReactivating = reactivatingSubscription === cancelledSub.id;

    return (
      <div className="space-y-3">
        {stillActive ? (
          <button
            onClick={() => onReactivateSubscription(cancelledSub.id)}
            disabled={isReactivating}
            className={clsx(
              'w-full rounded-lg px-4 py-2.5 text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-2 focus-visible:outline-offset-2',
              primaryBtnClass
            )}
          >
            {isReactivating ? (
              <span className="inline-flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Wird reaktiviert...
              </span>
            ) : (
              'Reaktivieren'
            )}
          </button>
        ) : (
          <button
            onClick={() => onSubscribe(plan.slug)}
            className={clsx(
              'w-full rounded-lg px-4 py-2.5 text-sm font-semibold transition-all focus-visible:outline-2 focus-visible:outline-offset-2',
              primaryBtnClass
            )}
          >
            Erneut abonnieren
          </button>
        )}
        {cancelledSub.currentPeriodEnd && (
          <p className="text-xs text-center text-yellow-500">
            Zugang bis: {new Date(cancelledSub.currentPeriodEnd).toLocaleDateString('de-DE')}
          </p>
        )}
      </div>
    );
  }

  // Kein Abo (Free-Plans wurden mit Migration 160 deaktiviert -- kein Code-Pfad mehr)
  return (
    <div className="space-y-3">
      <button
        onClick={() => onSubscribe(plan.slug)}
        className={clsx(
          'w-full rounded-lg px-4 py-2.5 text-sm font-semibold transition-all focus-visible:outline-2 focus-visible:outline-offset-2',
          primaryBtnClass
        )}
      >
        Jetzt abonnieren
      </button>
    </div>
  );
};

// ============================================
// BITCOIN ABO (LEGACY single-card section)
// Wird bei nur einem Plan verwendet (z.B. wenn nur Stacker oder nur Whale aktiv ist).
// Bei zwei aktiven Bitcoin-Plaenen rendert BitcoinTwoPlanSection darunter.
// ============================================
interface BitcoinSectionProps {
  plan: Plan;
  activeSub: Subscription | undefined;
  cancelledSub: Subscription | undefined;
  activeDiscount?: ActiveDiscount | null;
  onSubscribe: (slug: string, interval?: BillingInterval) => void;
  onCancelSubscription: (id: string) => void;
  onReactivateSubscription: (id: string) => void;
  cancellingSubscription?: string | null;
  reactivatingSubscription?: string | null;
}

const BitcoinSection: React.FC<BitcoinSectionProps> = ({
  plan,
  activeSub,
  cancelledSub,
  activeDiscount,
  onSubscribe,
  onCancelSubscription,
  onReactivateSubscription,
  cancellingSubscription,
  reactivatingSubscription,
}) => {
  const isActive = !!activeSub;
  const enabledFeatures = plan.features?.filter(f => f.isEnabled) || [];

  return (
    <section className="relative">
      <div className="glass-surface relative mx-auto max-w-2xl overflow-hidden rounded-3xl px-6 py-12 sm:p-8 lg:mx-0 lg:max-w-none lg:py-16 xl:px-20">
        {/* Hintergrund-Gradient-Blobs (innerhalb der Karte) - verteilt für flächigen Schimmer */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
          <div className="absolute -top-10 -left-16 h-64 w-64 rounded-full bg-[#F7931A] opacity-15 dark:opacity-20 blur-3xl" />
          <div className="absolute top-1/3 right-[-4rem] h-72 w-72 rounded-full bg-[#F1C40F] opacity-12 dark:opacity-18 blur-3xl" />
          <div className="absolute -bottom-16 left-1/3 h-64 w-80 rounded-full bg-[#F7931A] opacity-10 dark:opacity-15 blur-3xl" />
          <div className="absolute top-1/2 left-1/4 h-48 w-48 -translate-y-1/2 rounded-full bg-[#F1C40F] opacity-8 dark:opacity-12 blur-3xl" />
        </div>

        <div className="relative">
          {/* Header: Logo + Titel + Beschreibung */}
          <div className="flex items-stretch gap-4 sm:gap-6">
            {/* Logo - Glas-Tile mit volle Höhe von Titel bis Ende Beschreibung */}
            <div className="flex-none flex items-center justify-center w-24 sm:w-32 self-stretch rounded-2xl bg-white/40 dark:bg-white/[0.06] backdrop-blur-xl backdrop-saturate-150 ring-1 ring-inset ring-white/60 dark:ring-white/10 shadow-[0_8px_24px_-6px_rgba(0,0,0,0.25),inset_0_1px_0_0_rgba(255,255,255,0.8)] dark:shadow-[0_8px_24px_-6px_rgba(0,0,0,0.6),inset_0_1px_0_0_rgba(255,255,255,0.15)]">
              <img
                src={getImageUrl('/logos/crypto/btc.svg')}
                alt="Bitcoin"
                className="w-14 h-14 sm:w-20 sm:h-20"
              />
            </div>

            {/* Rechts: Titel + Status-Badges + Beschreibung */}
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                <h2 className="text-2xl font-semibold tracking-tight text-pretty text-gray-900 dark:text-white sm:text-4xl">
                  {plan.name}
                </h2>
                {isActive && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-status-success/10 px-3 py-1 text-xs font-semibold text-status-success">
                    <Check className="w-3 h-3" />
                    Aktiv
                  </span>
                )}
                {cancelledSub && !isActive && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-yellow-500/10 px-3 py-1 text-xs font-semibold text-yellow-500">
                    Gekündigt
                  </span>
                )}
              </div>

              {/* Beschreibung */}
              {plan.description && (
                <p className="mt-3 text-base/7 text-pretty text-gray-500 dark:text-gray-300">
                  {plan.description}
                </p>
              )}
            </div>
          </div>

          {/* Kündigungs-Hinweis */}
          {cancelledSub && !isActive && cancelledSub.currentPeriodEnd && (
            <div className="mt-4 rounded-lg bg-yellow-500/10 border border-yellow-500/20 px-4 py-3">
              <p className="text-sm text-yellow-600 dark:text-yellow-400">
                Dein Zugang bleibt aktiv bis zum <strong>{new Date(cancelledSub.currentPeriodEnd).toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' })}</strong>. Danach verlierst du den Zugriff auf alle Inhalte.
              </p>
            </div>
          )}

          {/* Features - volle Breite, responsive Spalten */}
          {enabledFeatures.length > 0 && (
            <ul
              role="list"
              className="mt-8 grid grid-cols-1 gap-x-8 gap-y-3 text-sm/7 text-gray-700 dark:text-gray-200 sm:grid-cols-2 lg:grid-cols-3"
            >
              {enabledFeatures.map((feature) => (
                <li key={feature.id} className="flex gap-x-3">
                  <Check aria-hidden="true" className="h-7 w-5 flex-none text-[#F7931A]" />
                  {feature.name}
                </li>
              ))}
            </ul>
          )}

          {/* Preis + Action nebeneinander */}
          <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-4">
            <PriceDisplay price={plan.priceMonthly} discount={activeDiscount} size="sm" />
            <div className="flex-auto max-w-xs">
              <ActionButtons
                plan={plan}
                activeSub={activeSub}
                cancelledSub={cancelledSub}
                isFeatured={true}
                onSubscribe={onSubscribe}
                onCancelSubscription={onCancelSubscription}
                onReactivateSubscription={onReactivateSubscription}
                cancellingSubscription={cancellingSubscription}
                reactivatingSubscription={reactivatingSubscription}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// BITCOIN PRICING SECTION (zwei Karten + Monat/Jahr-Toggle)
// ============================================
interface BitcoinTwoPlanSectionProps {
  plans: Plan[]; // bitcoin-stacker und bitcoin-whale, sortiert nach sort_order
  subscriptions: Subscription[];
  activeDiscount?: ActiveDiscount | null;
  onSubscribe: (slug: string, interval?: BillingInterval) => void;
  onCancelSubscription: (id: string) => void;
  onReactivateSubscription: (id: string) => void;
  onPlanChange?: (targetSlug: string) => void;
  onCancelPendingDowngrade?: () => void;
  onEditResourceSelection?: () => void;
  cancellingSubscription?: string | null;
  reactivatingSubscription?: string | null;
}

const BitcoinTwoPlanSection: React.FC<BitcoinTwoPlanSectionProps> = ({
  plans,
  subscriptions,
  activeDiscount,
  onSubscribe,
  onCancelSubscription,
  onReactivateSubscription,
  onPlanChange,
  onCancelPendingDowngrade,
  onEditResourceSelection,
  cancellingSubscription,
  reactivatingSubscription,
}) => {
  const [interval, setInterval] = React.useState<BillingInterval>('monthly');

  // Bestehende Bitcoin-Subscription des Users (egal welcher Plan):
  // (activeSubInterval wird unten ermittelt, useEffect synchronisiert es weiter unten)
  // Auch gekündigte Subs mit Restlaufzeit zählen, damit der User keinen
  // zweiten Plan derselben Familie kaufen, sondern downgraden kann.
  const bitcoinSlugs = plans.map(p => p.slug);
  const isStillActiveSub = (s: Subscription): boolean => {
    if (!bitcoinSlugs.includes(s.planSlug)) return false;
    if (s.status === 'active' && !s.cancelAtPeriodEnd) return true;
    // Gekündigt aber noch nicht abgelaufen -> User hat noch Zugriff
    if (s.cancelAtPeriodEnd && s.currentPeriodEnd && new Date(s.currentPeriodEnd) > new Date()) return true;
    if (s.status === 'cancelled' && s.currentPeriodEnd && new Date(s.currentPeriodEnd) > new Date()) return true;
    return false;
  };
  const activeBitcoinSub = subscriptions.find(isStillActiveSub);

  // Heuristik: Aus Periodenlaenge der aktiven Sub das Abrechnungsintervall ableiten
  // (Subscription-Type fuehrt das nicht mit, aber Yearly hat ~365 Tage Periode).
  const activeSubInterval: BillingInterval | null = (() => {
    if (!activeBitcoinSub?.currentPeriodEnd) return null;
    const startStr = activeBitcoinSub.currentPeriodStart;
    if (!startStr) return null;
    const ms = new Date(activeBitcoinSub.currentPeriodEnd).getTime() - new Date(startStr).getTime();
    const days = ms / (1000 * 60 * 60 * 24);
    return days > 200 ? 'yearly' : 'monthly';
  })();
  const intervalIsLocked = !!activeSubInterval;

  // Wenn der User schon ein Abo hat, das Intervall des Abos synchron halten -- der
  // Toggle ist dann gesperrt, weil ein Plan-Wechsel zwischen Intervallen nicht erlaubt ist.
  React.useEffect(() => {
    if (activeSubInterval && activeSubInterval !== interval) {
      setInterval(activeSubInterval);
    }
  }, [activeSubInterval, interval]);

  // Pruefen ob mindestens ein Plan einen Jahrespreis hat (Toggle nur dann anzeigen)
  const hasAnyYearly = plans.some(p => p.priceYearly !== null && p.priceYearly !== undefined);

  // Jährliche Ersparnis (max über alle Pläne) als Hinweis am Toggle
  const maxYearlySavingPct = plans.reduce((max, p) => {
    if (!p.priceYearly || p.priceMonthly <= 0) return max;
    const yearlyMonthly = p.priceYearly / 12;
    const saving = (1 - yearlyMonthly / p.priceMonthly) * 100;
    return Math.max(max, saving);
  }, 0);

  return (
    <section className="relative isolate">
      {/* Bitcoin-Hintergrund-Gradient — selbst abgerundet & geclipt,
          damit der Schatten der glass-surface aussen sichtbar bleibt */}
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10 overflow-hidden sm:rounded-3xl"
      >
        <div
          aria-hidden="true"
          className="absolute inset-x-0 -top-16 flex transform-gpu justify-center blur-3xl"
        >
          <div
            style={{
              clipPath:
                'polygon(73.6% 51.7%, 91.7% 11.8%, 100% 46.4%, 97.4% 82.2%, 92.5% 84.9%, 75.7% 64%, 55.3% 47.5%, 46.5% 49.4%, 45% 62.9%, 50.3% 87.2%, 21.3% 64.1%, 0.1% 100%, 5.4% 51.1%, 21.4% 63.9%, 58.9% 0.2%, 73.6% 51.7%)',
            }}
            className="aspect-[1318/752] w-[82.375rem] flex-none bg-gradient-to-r from-[#F7931A] to-[#F1C40F] opacity-10 dark:opacity-15"
          />
        </div>
      </div>

      <div className="glass-surface-soft px-6 py-12 sm:rounded-3xl sm:px-8 lg:px-12 lg:py-16">
        {/* Ueberschrift */}
        <div className="text-center mb-8">
          <h2 className="text-3xl font-semibold tracking-tight text-pretty text-gray-900 dark:text-white sm:text-4xl">
            RENDITEFUCHS Blog
          </h2>
          <p className="mt-3 text-base text-gray-500 dark:text-gray-300 max-w-xl mx-auto">
            Bitcoin-Analysen, Strategien und Premium-Inhalte. Wähle dein Paket.
          </p>
        </div>

        {/* Monat/Jahr-Toggle */}
        {hasAnyYearly && (
          <div className="flex flex-col items-center justify-center gap-2 mb-10">
            <div
              role="tablist"
              aria-label="Abrechnungsintervall"
              className="relative inline-grid grid-cols-2 items-center rounded-full p-1 bg-gray-100 dark:bg-gray-800 ring-1 ring-gray-200 dark:ring-gray-700"
            >
              {/* Animierte Pille hinter den Buttons */}
              <span
                aria-hidden="true"
                className={clsx(
                  'absolute top-1 bottom-1 left-1 w-[calc(50%-0.25rem)] rounded-full bg-brand shadow-sm',
                  'transition-transform duration-300 ease-out',
                  interval === 'yearly' ? 'translate-x-full' : 'translate-x-0'
                )}
              />
              <button
                role="tab"
                type="button"
                aria-selected={interval === 'monthly'}
                disabled={intervalIsLocked}
                onClick={() => !intervalIsLocked && setInterval('monthly')}
                className={clsx(
                  'relative z-10 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors',
                  interval === 'monthly'
                    ? 'text-gray-950'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200',
                  intervalIsLocked && 'opacity-60 cursor-not-allowed hover:text-gray-500'
                )}
              >
                Monatlich
              </button>
              <button
                role="tab"
                type="button"
                aria-selected={interval === 'yearly'}
                disabled={intervalIsLocked}
                onClick={() => !intervalIsLocked && setInterval('yearly')}
                className={clsx(
                  'relative z-10 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors',
                  interval === 'yearly'
                    ? 'text-gray-950'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200',
                  intervalIsLocked && 'opacity-60 cursor-not-allowed hover:text-gray-500'
                )}
              >
                Jährlich
                {!intervalIsLocked && maxYearlySavingPct >= 1 && (
                  <span className="absolute -top-2 -right-2 z-20 rounded-full bg-status-success px-1.5 py-0.5 text-[10px] font-bold text-white shadow-sm leading-none">
                    -{Math.round(maxYearlySavingPct)}%
                  </span>
                )}
              </button>
            </div>
            {intervalIsLocked && (
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                Du hast bereits ein {activeSubInterval === 'yearly' ? 'Jahres' : 'Monats'}abo. Wechsel zwischen Intervallen ist nicht möglich.
              </span>
            )}
          </div>
        )}

        {/* Karten */}
        <div className="isolate mx-auto grid max-w-md grid-cols-1 gap-y-8 gap-x-8 lg:max-w-3xl lg:grid-cols-2">
          {plans.map((plan) => {
            const activeSub = subscriptions.find(
              s => s.planSlug === plan.slug &&
              s.status === 'active' &&
              !s.cancelAtPeriodEnd
            );
            const cancelledSub = subscriptions.find(
              s => s.planSlug === plan.slug && s.cancelAtPeriodEnd
            );
            const isActive = !!activeSub;
            const enabledFeatures = plan.features?.filter(f => f.isEnabled) || [];

            // Preis für aktuelles Intervall
            const showYearly = interval === 'yearly' && plan.priceYearly !== null && plan.priceYearly !== undefined;
            const yearlyMonthlyEquivalent = showYearly
              ? Math.round((plan.priceYearly as number) / 12 * 100) / 100
              : null;
            // Wie viele volle Monate spart der User mit dem Jahresabo?
            // Ersparnis = 12 × Monatspreis − Jahrespreis, geteilt durch Monatspreis, abgerundet.
            const freeMonthsYearly = showYearly && plan.priceMonthly > 0
              ? Math.floor((12 * plan.priceMonthly - (plan.priceYearly as number)) / plan.priceMonthly)
              : 0;

            const isHighlighted = !!(plan.badgeEnabled && plan.badgeText);

            return (
              <div
                key={plan.id}
                className={clsx(
                  'glass-surface-soft relative flex flex-col rounded-3xl p-8 xl:p-10',
                  isActive
                    ? '!ring-2 !ring-green-500'
                    : isHighlighted
                      ? '!ring-2 !ring-[#F7931A]'
                      : ''
                )}
              >
                {/* Badge: überlappend an der Oberkante */}
                {!isActive && plan.badgeEnabled && plan.badgeText && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-[#F7931A] px-3 py-1 text-xs font-semibold text-gray-950 shadow-sm whitespace-nowrap">
                    {plan.badgeText}
                  </span>
                )}
                {isActive && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-status-success px-3 py-1 text-xs font-semibold text-gray-950 shadow-sm whitespace-nowrap">
                    Aktiv
                  </span>
                )}
                <div className="flex-1">
                  {/* 1. Plan Header (Name) */}
                  <div className="flex items-center gap-x-4">
                    <h3 className={clsx(
                      'text-lg/8 font-semibold',
                      isHighlighted ? 'text-[#F7931A]' : 'text-gray-900 dark:text-white'
                    )}>
                      {plan.name}
                    </h3>
                  </div>

                  {/* 2. Beschreibung */}
                  {plan.description && (
                    <p className="mt-4 text-sm/6 text-gray-600 dark:text-gray-300">{plan.description}</p>
                  )}

                  {/* 3. Features (vollständig aus billing.plan_features) */}
                  <ul role="list" className="mt-6 space-y-3 text-sm/6 text-gray-600 dark:text-gray-300">
                    {enabledFeatures.map((feature) => (
                      <li key={feature.id} className="flex gap-x-3">
                        <Check aria-hidden="true" className={clsx('h-6 w-5 flex-none', isHighlighted ? 'text-[#F7931A]' : 'text-[#F7931A]/80')} />
                        {formatFeatureLabel(feature)}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* 4. Preis (zentriert, mit goldenem Fade-Trennstrich) */}
                <div className="relative mt-8 pt-6 flex flex-col items-center text-center
                  before:absolute before:inset-x-0 before:top-0 before:h-px
                  before:bg-gradient-to-r before:from-transparent before:via-brand/60 before:to-transparent">
                  {showYearly && plan.priceYearly !== null && plan.priceYearly !== undefined ? (
                    <>
                      <div className="relative inline-block">
                        <PriceDisplay
                          price={plan.priceYearly}
                          discount={activeDiscount}
                          size="lg"
                          intervalSuffix="/Jahr"
                        />
                        {freeMonthsYearly > 0 && (
                          <span
                            className="absolute bottom-1 -right-3 translate-x-full rotate-[8deg] origin-bottom-left
                              inline-flex items-center gap-1 rounded-full bg-brand px-2.5 py-1 text-[11px] font-bold text-gray-950
                              shadow-[0_2px_6px_-2px_rgba(0,0,0,0.25)] ring-1 ring-brand/50 whitespace-nowrap"
                          >
                            <Tag className="w-3 h-3" />
                            {freeMonthsYearly} {freeMonthsYearly === 1 ? 'Monat' : 'Monate'} gratis
                          </span>
                        )}
                      </div>
                      {yearlyMonthlyEquivalent !== null && (
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          (entspricht {formatPrice(yearlyMonthlyEquivalent)}/Monat)
                        </p>
                      )}
                    </>
                  ) : (
                    <PriceDisplay
                      price={plan.priceMonthly}
                      discount={activeDiscount}
                      size="lg"
                      intervalSuffix="/Monat"
                    />
                  )}
                </div>

                {/* 5. Action */}
                <div className="mt-6">
                  <ActionButtons
                    plan={plan}
                    activeSub={activeSub}
                    cancelledSub={cancelledSub}
                    isFeatured={isHighlighted}
                    onSubscribe={(slug) => onSubscribe(slug, interval)}
                    onCancelSubscription={onCancelSubscription}
                    onReactivateSubscription={onReactivateSubscription}
                    onPlanChange={onPlanChange}
                    onCancelPendingDowngrade={onCancelPendingDowngrade}
                    onEditResourceSelection={onEditResourceSelection}
                    cancellingSubscription={cancellingSubscription}
                    reactivatingSubscription={reactivatingSubscription}
                    hasOtherFcmsActive={!!activeBitcoinSub && activeBitcoinSub.planSlug !== plan.slug}
                    otherFcmsActiveSub={activeBitcoinSub && activeBitcoinSub.planSlug !== plan.slug ? activeBitcoinSub : undefined}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ============================================
// FCMS USAGE STATS (Nutzungsanzeige)
// ============================================
interface UsageBarItem {
  label: string;
  current: number;
  limit: number | null;
}

interface FcmsUsageStatsProps {
  limits: FcmsFeatureLimits;
}

const FcmsUsageStats: React.FC<FcmsUsageStatsProps> = ({ limits }) => {
  const { features, tier } = limits;
  const isUnlimited = tier === 'unlimited' || tier === 'admin';

  const items: UsageBarItem[] = [
    { label: 'Watchlists', current: features.watchlists.current, limit: features.watchlists.limit },
    { label: 'Assets', current: features.assets.current, limit: features.assets.limit },
    { label: 'YouTube-Kanäle', current: features.youtubeChannels.current, limit: features.youtubeChannels.limit },
    { label: 'Chart-Layouts', current: features.chartLayouts.current, limit: features.chartLayouts.limit },
  ];

  const getBarColor = (current: number, limit: number | null): string => {
    if (limit === null || limit === 0) return 'bg-brand';
    const percent = (current / limit) * 100;
    if (percent >= 100) return 'bg-red-500';
    if (percent >= 85) return 'bg-orange-500';
    if (percent >= 60) return 'bg-brand';
    return 'bg-green-500';
  };

  return (
    <div className="mt-6 pt-5 border-t border-gray-200 dark:border-gray-700">
      <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">
        Deine Nutzung
      </p>
      <div className="space-y-3">
        {items.map((item) => {
          const hasLimit = !isUnlimited && item.limit !== null;
          const percent = hasLimit && item.limit! > 0
            ? Math.round((item.current / item.limit!) * 100)
            : 0;

          return (
            <div key={item.label}>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-gray-600 dark:text-gray-300">{item.label}</span>
                <span className="font-medium text-gray-700 dark:text-gray-200">
                  {hasLimit
                    ? <>{item.current} <span className="text-gray-400 dark:text-gray-500">/ {item.limit}</span></>
                    : <>{item.current} <span className="text-gray-400 dark:text-gray-500">/ unbegrenzt</span></>
                  }
                </span>
              </div>
              {hasLimit && (
                <div className="h-1.5 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                  <div
                    className={clsx(
                      'h-full rounded-full transition-all duration-500',
                      getBarColor(item.current, item.limit)
                    )}
                    style={{
                      width: `${Math.min(percent, 100)}%`
                    }}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ============================================
// FCMS PRICING SECTION (3-spaltig)
// ============================================
interface FcmsSectionProps {
  plans: Plan[];
  subscriptions: Subscription[];
  fcmsLimits?: FcmsFeatureLimits | null;
  activeDiscount?: ActiveDiscount | null;
  onSubscribe: (slug: string) => void;
  onCancelSubscription: (id: string) => void;
  onReactivateSubscription: (id: string) => void;
  onPlanChange?: (targetSlug: string) => void;
  onCancelPendingDowngrade?: () => void;
  onEditResourceSelection?: () => void;
  cancellingSubscription?: string | null;
  reactivatingSubscription?: string | null;
}

const FcmsSection: React.FC<FcmsSectionProps> = ({
  plans,
  subscriptions,
  fcmsLimits,
  activeDiscount,
  onSubscribe,
  onCancelSubscription,
  onReactivateSubscription,
  onPlanChange,
  onCancelPendingDowngrade,
  onEditResourceSelection,
  cancellingSubscription,
  reactivatingSubscription,
}) => {
  // Bestehende FCMS-Subscription (egal welcher Plan).
  // Auch gekündigte Subs mit Restlaufzeit zählen, damit der User in dieser
  // Phase keinen zweiten Plan kaufen, sondern downgraden kann.
  const fcmsSlugs = ['fcms-pro', 'fcms-unlimited'];
  const activeFcmsSub = subscriptions.find(s => {
    if (!fcmsSlugs.includes(s.planSlug)) return false;
    if (s.status === 'active' && !s.cancelAtPeriodEnd) return true;
    if (s.cancelAtPeriodEnd && s.currentPeriodEnd && new Date(s.currentPeriodEnd) > new Date()) return true;
    if (s.status === 'cancelled' && s.currentPeriodEnd && new Date(s.currentPeriodEnd) > new Date()) return true;
    return false;
  });
  return (
    <section className="relative isolate">
      {/* Hintergrund-Gradient — selbst abgerundet & geclipt,
          damit der Schatten der glass-surface aussen sichtbar bleibt */}
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10 overflow-hidden sm:rounded-3xl"
      >
        <div
          aria-hidden="true"
          className="absolute inset-x-0 -top-16 flex transform-gpu justify-center blur-3xl"
        >
          <div
            style={{
              clipPath:
                'polygon(73.6% 51.7%, 91.7% 11.8%, 100% 46.4%, 97.4% 82.2%, 92.5% 84.9%, 75.7% 64%, 55.3% 47.5%, 46.5% 49.4%, 45% 62.9%, 50.3% 87.2%, 21.3% 64.1%, 0.1% 100%, 5.4% 51.1%, 21.4% 63.9%, 58.9% 0.2%, 73.6% 51.7%)',
            }}
            className="aspect-[1318/752] w-[82.375rem] flex-none bg-gradient-to-r from-[#F1C40F] to-[#D4AC0D] opacity-10 dark:opacity-15"
          />
        </div>
      </div>

      <div className="glass-surface-soft px-6 py-12 sm:rounded-3xl sm:px-8 lg:px-12 lg:py-16">
        {/* Überschrift */}
        <h2 className="text-3xl font-semibold tracking-tight text-pretty text-gray-900 dark:text-white sm:text-4xl text-center mb-10">
          Financial Content Management System
        </h2>

        {/* Karten - unverändert */}
        <div className="isolate mx-auto grid max-w-md grid-cols-1 gap-y-8 lg:mx-0 lg:max-w-none lg:grid-cols-3">
        {plans.map((plan, idx) => {
          const activeSub = subscriptions.find(
            s => s.planSlug === plan.slug &&
            s.status === 'active' &&
            !s.cancelAtPeriodEnd
          );
          const cancelledSub = subscriptions.find(
            s => s.planSlug === plan.slug && s.cancelAtPeriodEnd
          );
          const isActive = !!activeSub;
          const isMostPopular = !!(plan.badgeEnabled && plan.badgeText);
          const enabledFeatures = plan.features?.filter(f => f.isEnabled) || [];
          const disabledFeatures = plan.features?.filter(f => !f.isEnabled) || [];

          return (
            <div
              key={plan.id}
              className={clsx(
                'glass-surface-soft flex flex-col justify-between rounded-3xl p-8 xl:p-10',
                // Ring/Border
                isActive
                  ? '!ring-2 !ring-green-500 lg:z-10'
                  : isMostPopular
                    ? '!ring-2 !ring-brand lg:z-10 lg:rounded-b-none'
                    : '',
                // Abrundung für äußere Karten auf Desktop
                !isMostPopular && !isActive && 'lg:mt-8',
                !isActive && idx === 0 && 'lg:rounded-r-none',
                !isActive && idx === plans.length - 1 && 'lg:rounded-l-none',
              )}
            >
              <div>
                {/* Plan Header */}
                <div className="flex items-center justify-between gap-x-4">
                  <h3
                    className={clsx(
                      'text-lg/8 font-semibold',
                      isMostPopular ? 'text-brand' : 'text-gray-900 dark:text-white'
                    )}
                  >
                    {plan.name}
                  </h3>
                  {isActive && (
                    <span className="rounded-full bg-status-success/10 px-2.5 py-1 text-xs font-semibold text-status-success">
                      Aktiv
                    </span>
                  )}
                  {!isActive && plan.badgeEnabled && plan.badgeText && (
                    <span className="rounded-full bg-brand/25 px-2.5 py-1 text-xs font-semibold text-brand">
                      {plan.badgeText}
                    </span>
                  )}
                </div>

                {/* Description */}
                {plan.description && (
                  <p className="mt-4 text-sm/6 text-gray-600 dark:text-gray-300">{plan.description}</p>
                )}

                {/* Price */}
                <div className="mt-6">
                  <PriceDisplay price={plan.priceMonthly} discount={activeDiscount} size="lg" />
                </div>

                {/* Enabled Features */}
                <ul role="list" className="mt-8 space-y-3 text-sm/6 text-gray-600 dark:text-gray-300">
                  {enabledFeatures.map((feature) => (
                    <li key={feature.id} className="flex gap-x-3">
                      <Check
                        aria-hidden="true"
                        className={clsx(
                          'h-6 w-5 flex-none',
                          isMostPopular ? 'text-brand' : 'text-brand/80'
                        )}
                      />
                      {formatFeatureLabel(feature)}
                    </li>
                  ))}
                  {/* Deaktivierte Features durchgestrichen */}
                  {disabledFeatures.map((feature) => (
                    <li key={feature.id} className="flex gap-x-3 text-gray-400 dark:text-gray-500">
                      <X
                        aria-hidden="true"
                        className="h-6 w-5 flex-none text-gray-300 dark:text-gray-600"
                      />
                      <span className="line-through">{feature.name}</span>
                    </li>
                  ))}
                </ul>

                {/* Usage Stats - auf aktiver Karte oder bei hasFcmsSubscription auf passendem Plan */}
                {fcmsLimits && (
                  isActive ||
                  (fcmsLimits.hasFcmsSubscription && fcmsLimits.planId && plan.id === fcmsLimits.planId) ||
                  (fcmsLimits.hasFcmsSubscription && !fcmsLimits.planId && plan.slug === 'fcms-unlimited')
                ) && (
                  <FcmsUsageStats limits={fcmsLimits} />
                )}
              </div>

              {/* Action Button */}
              <div className="mt-8">
                <ActionButtons
                  plan={plan}
                  activeSub={activeSub}
                  cancelledSub={cancelledSub}
                  isFeatured={false}
                  onSubscribe={onSubscribe}
                  onCancelSubscription={onCancelSubscription}
                  onReactivateSubscription={onReactivateSubscription}
                  onPlanChange={onPlanChange}
                  onCancelPendingDowngrade={onCancelPendingDowngrade}
                  onEditResourceSelection={onEditResourceSelection}
                  cancellingSubscription={cancellingSubscription}
                  reactivatingSubscription={reactivatingSubscription}
                  hasOtherFcmsActive={!!activeFcmsSub && activeFcmsSub.planSlug !== plan.slug}
                  otherFcmsActiveSub={activeFcmsSub && activeFcmsSub.planSlug !== plan.slug ? activeFcmsSub : undefined}
                />
              </div>
            </div>
          );
        })}
        </div>
      </div>
    </section>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================
export const SubscriptionCards: React.FC<SubscriptionCardsProps> = ({
  plans,
  subscriptions,
  fcmsLimits,
  activeDiscount,
  onSubscribe,
  onCancelSubscription,
  onReactivateSubscription,
  onPlanChange,
  onCancelPendingDowngrade,
  onEditResourceSelection,
  cancellingSubscription,
  reactivatingSubscription,
}) => {
  // Pläne aufteilen: Bitcoin (Stacker + Whale) vs FCMS (pro + unlimited)
  const bitcoinPlans = plans
    .filter(p => p.slug === 'bitcoin-stacker' || p.slug === 'bitcoin-whale')
    .sort((a, b) => a.priceMonthly - b.priceMonthly);
  const fcmsPlans = plans.filter(p =>
    p.slug === 'fcms-pro' || p.slug === 'fcms-unlimited'
  );

  return (
    <div className="space-y-12">
      {/* Sektion 1: RENDITEFUCHS BLOG (Bitcoin Stacker + Whale) */}
      {bitcoinPlans.length > 0 && (
        <BitcoinTwoPlanSection
          plans={bitcoinPlans}
          subscriptions={subscriptions}
          activeDiscount={activeDiscount}
          onSubscribe={onSubscribe}
          onCancelSubscription={onCancelSubscription}
          onReactivateSubscription={onReactivateSubscription}
          onPlanChange={onPlanChange}
          onCancelPendingDowngrade={onCancelPendingDowngrade}
          onEditResourceSelection={onEditResourceSelection}
          cancellingSubscription={cancellingSubscription}
          reactivatingSubscription={reactivatingSubscription}
        />
      )}

      {/* Sektion 2: FCMS Pläne */}
      {fcmsPlans.length > 0 && (
        <FcmsSection
          plans={fcmsPlans}
          subscriptions={subscriptions}
          fcmsLimits={fcmsLimits}
          activeDiscount={activeDiscount}
          onSubscribe={onSubscribe}
          onCancelSubscription={onCancelSubscription}
          onReactivateSubscription={onReactivateSubscription}
          onPlanChange={onPlanChange}
          onCancelPendingDowngrade={onCancelPendingDowngrade}
          onEditResourceSelection={onEditResourceSelection}
          cancellingSubscription={cancellingSubscription}
          reactivatingSubscription={reactivatingSubscription}
        />
      )}
    </div>
  );
};

export default SubscriptionCards;
